import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { StyleSheet, Pressable, RefreshControl, ActivityIndicator, View, Platform, ScrollView } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useCollectionStore, useModalStore, useToastStore, useAuthStore, useFileStore, useChatStore, useChatInputStore, useInferenceTasksStore, useCoworkerStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ChatInput from '@/components/ChatInput/ChatInput';
import ComposerModeSwitch from '@/components/ChatInput/ComposerModeSwitch';
import WorkModeIntentSuggestions from '@/components/WorkModeIntentSuggestions';
import WorkSuggestionChips from '@/components/Coworker/WorkSuggestionChips';
import useWorkStarterPrompts from '@/hooks/useWorkStarterPrompts';
import { WORK_COMPOSER_PLACEHOLDER } from '@/constants/work';
import useRequireAuth from '@/hooks/useRequireAuth';
import CollectionChatItem from '@/components/CollectionChatItem';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import useCollectionContentSort from '@/hooks/useCollectionContentSort';
import {
  useAuthHydrated,
  useAuthPresentation,
} from '@/hooks/useRequireAuth';
import StatusContainer from '@/components/StatusContainer';
import { CHAT_INPUT_STYLE, BORDER_RADIUS } from '@/constants/config';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';
import socketManager from '@/lib/socket';
import { focusChatInput } from '@/lib/chatInputFocus';
import MetaHead from '@/components/MetaHead';
import TaskCard from '@/components/Tasks/TaskCard';
import CollectionDropdown from '@/components/Dropdowns/CollectionDropdown';
import { icons as collectionIcons } from '@/components/CollectionIconsGrid';
import FilesGrid from '@/components/Files/FilesGrid';
import FilesTable from '@/components/Table/FilesTable';
import FloatingSelectButton from '@/components/Buttons/FloatingSelectButton';
import Button from '@/components/Buttons/Button';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import Toggle from '@/components/Inputs/Toggle';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { getAncestorIds, getDescendantIds, getItemDepth, MAX_FOLDER_DEPTH, normalizeParentId } from '@/constants/utils/folder_tree';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';
import reportWorkLaunchError from '@/lib/reportWorkLaunchError';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';
import { navigateToCreatedWork } from '@/lib/startWorkFromPrompt';

const VIEW_MODES = {
  GRID: 'grid',
  LIST: 'list',
};


const loadingStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const createStyles = (colors, isXS, isXL, hideHeaderBorder, insetBottom = 0) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  contentWrapper: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    ...(Platform.OS === 'web' && { position: 'sticky' }),
    top: 0,
    zIndex: 10,
    backgroundColor: colors.solid_color_primary,
    width: '100%',
    borderBottomWidth: hideHeaderBorder ? 0 : 1,
    borderBottomColor: hideHeaderBorder ? 'transparent' : colors.border_color_secondary,
  },
  headerContent: {
    width: '100%',
    maxWidth: isXL ? 1000 : 768,
    alignSelf: 'center',
    paddingTop: 8,
    paddingBottom: 8,
  },
  breadcrumbContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingBottom: 8,
    rowGap: 4,
  },
  breadcrumbButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: BORDER_RADIUS.small,
    maxWidth: '100%',
    flexShrink: 1,
  },
  breadcrumbButtonHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
  breadcrumbSeparator: {
    marginRight: 4,
    flexShrink: 0,
  },
  breadcrumbLabel: {
    flexShrink: 1,
    minWidth: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: isXS ? 4 : 8,
    paddingBottom: isXS ? 16 : 20,
    gap: 12,
  },
  titleIdentity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  titleActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  titleActionButton: {
    width: 36,
    height: 36,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_block,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border_color_secondary,
  },
  titleActionButtonHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
  scrollContainer: {
    flex: 1,
    width: '100%',
  },
  scrollContainerContent: {
    width: '100%',
    paddingBottom: insetBottom,
  },
  content: {
    paddingHorizontal: isXS ? 12 : 20,
    paddingTop: isXS ? 4 : 8,
    paddingBottom: isXS ? 12 : 20,
    maxWidth: isXL ? 1000 : 768,
    alignSelf: 'center',
    width: '100%',
  },
  // Collection view styles
  collectionSection: {
    marginBottom: 20,
  },
  collectionSectionTitle: {
    color: colors.text_color_secondary,
    fontSize: 12,
    marginBottom: 12,
  },
  collectionDivider: {
    height: 1,
    backgroundColor: colors.border_color_secondary,
    marginVertical: 16,
  },
  emptyChatsContainer: {
    paddingVertical: 48,
    alignItems: 'center',
    gap: 6,
  },
  chatsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  chatsHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  chatsList: {
    gap: 8,
  },
  allFilesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: isXS ? 12 : 8,
  },
  emptyFilesContainer: {
    backgroundColor: colors.solid_color_block,
    borderRadius: BORDER_RADIUS.medium,
    padding: 20,
    alignItems: 'center',
    gap: 8,
  },
  modeSwitch: {
    alignItems: 'center',
    marginBottom: 4,
  },
  workSuggestionChips: {
    width: '100%',
    marginBottom: 10,
  },
  nestedSection: {
    marginBottom: 16,
    gap: 8,
  },
  nestedHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  nestedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
  },
  nestedRowHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
  nestedAddButton: {
    padding: 6,
    borderRadius: BORDER_RADIUS.small,
  },
  // Tabs
  tabsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 12,
  },
  tabsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  tabButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  tabButtonHover: {
    backgroundColor: colors.solid_color_secondary,
  },
  tabButtonActive: {
    backgroundColor: colors.solid_color_tertiary,
  },
  toolbarButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderRadius: BORDER_RADIUS.small,
  },
  toolbarButtonHovered: {
    backgroundColor: colors.solid_color_secondary,
  },
  toolbarButtonActive: {
    backgroundColor: colors.solid_color_tertiary,
  },
});

const CollectionToolbarButton = React.memo(({
  icon,
  appendIcon,
  label,
  onPress,
  isActive,
  accessibilityLabel,
  colors,
  styles,
}) => (
  <Pressable
    onPress={onPress}
    style={({ hovered }) => [
      styles.toolbarButton,
      isActive && styles.toolbarButtonActive,
      hovered && styles.toolbarButtonHovered,
      Platform.OS === 'web' ? { cursor: 'pointer' } : null,
    ]}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel || label}
    accessibilityState={isActive ? { selected: true } : undefined}
    hitSlop={8}
  >
    {icon ? <Icon name={icon} size={13} color={colors.text_color_secondary} /> : null}
    {label ? (
      <Text size="s" color="secondary" weight="medium">
        {label}
      </Text>
    ) : null}
    {appendIcon ? <Icon name={appendIcon} size={9} color={colors.text_color_tertiary} /> : null}
  </Pressable>
));
CollectionToolbarButton.displayName = 'CollectionToolbarButton';

// Collection Tasks Section
const CollectionTasksSection = React.memo(({
  collectionId,
  colors,
  isXS,
  onCreateTask,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isXS, false, false, 0), [colors, isXS]);
  const tasksMap = useInferenceTasksStore(state => state.tasks);
  const taskIds = useInferenceTasksStore(state => state.taskIds);
  const fetchTasks = useInferenceTasksStore(state => state.fetchTasks);
  const isLoadingTasks = useInferenceTasksStore(state => state.isLoading.fetch);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchTasks(1);
    }
  }, [fetchTasks]);

  const collectionTasks = useMemo(() => {
    if (!collectionId) return [];
    const cid = String(collectionId);
    return taskIds
      .map(id => tasksMap[id])
      .filter(task => {
        if (!task) return false;
        const taskColl = task.collectionId || task.collection?.id || task.collection;
        return taskColl && String(taskColl) === cid;
      });
  }, [tasksMap, taskIds, collectionId]);

  if (embedded && collectionTasks.length === 0) {
    return (
      <View style={styles.emptyFilesContainer}>
        <Icon name="clock" size={24} color={colors.text_color_tertiary} />
        <Text size="s" color="secondary">
          {t('pages.collections.createTaskDescription')}
        </Text>
        <NavigationBarButton
          icon="clock"
          label={t('pages.collections.createTask')}
          onPress={onCreateTask}
          isActive
        />
      </View>
    );
  }

  if (collectionTasks.length === 0 && !isLoadingTasks) return null;

  return (
    <View style={embedded ? null : styles.collectionSection}>
      {!embedded && <View style={styles.collectionDivider} />}

      {!embedded && (
        <View style={styles.chatsHeader}>
          <View style={styles.chatsHeaderLeft}>
            <Icon name="clock" size={14} color={colors.text_color_secondary} />
            <Text size="s" weight="medium" color="secondary">
              {t('pages.collections.tasks')}
            </Text>
            {collectionTasks.length > 0 && (
              <Text size="xs" color="tertiary">
                ({collectionTasks.length})
              </Text>
            )}
          </View>
        </View>
      )}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        {collectionTasks.map((task) => (
          <TaskCard key={task.id} task={task} showActions />
        ))}
      </View>
    </View>
  );
});

// Collection Chats Section
const CollectionChatsSection = React.memo(({ 
  collection,
  colors, 
  isXS,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isXS, false, false, 0), [colors, isXS]);
  const allChats = useChatStore(state => state.chats);
  const pinnedConversations = useAuthStore(state => state.user?.pinnedConversations);
  const pinnedIdSet = useMemo(
    () => new Set((pinnedConversations || []).map((id) => String(id?.id ?? id?._id ?? id)).filter(Boolean)),
    [pinnedConversations],
  );
  const [chatSortOrder, setChatSortOrder] = useCollectionContentSort(collection?.id, 'chats');

  // Multi-select state lives in the chat store so the same selection model
  // backs both the library sidebar and the collection page (lets us reuse
  // bulk delete / archive / add-to-collection store actions verbatim).
  const isSelectingMultiple = useChatStore(state => state.isSelectingMultipleChats);
  const selectedChatIds = useChatStore(state => state.selectedChatIds);
  const startSelectingMultipleChats = useChatStore(state => state.startSelectingMultipleChats);
  const stopSelectingMultipleChats = useChatStore(state => state.stopSelectingMultipleChats);
  const toggleSelectChatForMulti = useChatStore(state => state.toggleSelectChatForMulti);

  const CHAT_SORT_OPTIONS = useMemo(() => [
    { id: 'newest', label: t('pages.common.newestFirst'), icon: 'arrow-down-wide-short' },
    { id: 'oldest', label: t('pages.common.oldestFirst'), icon: 'arrow-up-wide-short' },
    { id: 'a-z', label: t('pages.common.nameAZ'), icon: 'arrow-down-a-z' },
    { id: 'z-a', label: t('pages.common.nameZA'), icon: 'arrow-up-z-a' },
  ], [t]);

  const conversationIds = collection?.conversationIds || [];
  
  const collectionChats = useMemo(() => {
    if (!conversationIds.length) return [];
    const chats = conversationIds
      .map(conversationId => allChats.find(chat => chat.id === conversationId))
      .filter(Boolean);

    return [...chats].sort((a, b) => {
      const getLastActivityDate = (chat) => {
        const lastMsg = chat.messages?.[chat.messages.length - 1];
        return new Date(lastMsg?.timestamp || chat.updatedAt || chat.createdAt || 0);
      };
      // Auth pin list is the source of truth so unpin immediately re-sorts
      // the conversation below the pinned group.
      const aPinned = pinnedIdSet.has(String(a.id));
      const bPinned = pinnedIdSet.has(String(b.id));
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      switch (chatSortOrder) {
        case 'newest':
          return getLastActivityDate(b) - getLastActivityDate(a);
        case 'oldest':
          return getLastActivityDate(a) - getLastActivityDate(b);
        case 'a-z':
          return (a.title || '').localeCompare(b.title || '');
        case 'z-a':
          return (b.title || '').localeCompare(a.title || '');
        default:
          return 0;
      }
    });
  }, [conversationIds, allChats, chatSortOrder, pinnedIdSet]);

  const currentChatSort = CHAT_SORT_OPTIONS.find(o => o.id === chatSortOrder) || CHAT_SORT_OPTIONS[0];

  const handleToggleSelectMode = useCallback(() => {
    if (isSelectingMultiple) {
      stopSelectingMultipleChats();
    } else {
      startSelectingMultipleChats();
    }
  }, [isSelectingMultiple, startSelectingMultipleChats, stopSelectingMultipleChats]);

  const handleSelectAll = useCallback(() => {
    const allIds = collectionChats.map(c => c.id);
    const everySelected = allIds.length > 0 && allIds.every(cid => selectedChatIds.includes(cid));
    if (everySelected) {
      // Clear only this collection's selections; leave any cross-context picks.
      allIds.forEach(cid => {
        if (selectedChatIds.includes(cid)) toggleSelectChatForMulti(cid);
      });
    } else {
      allIds.forEach(cid => {
        if (!selectedChatIds.includes(cid)) toggleSelectChatForMulti(cid);
      });
    }
  }, [collectionChats, selectedChatIds, toggleSelectChatForMulti]);

  const allSelectedInView = useMemo(() => {
    if (collectionChats.length === 0) return false;
    return collectionChats.every(c => selectedChatIds.includes(c.id));
  }, [collectionChats, selectedChatIds]);

  return (
    <View style={embedded ? null : styles.collectionSection}>
      {!embedded && <View style={styles.collectionDivider} />}

      <View style={[styles.chatsHeader, embedded && { justifyContent: 'flex-end' }]}>
        {!embedded && (
          <View style={styles.chatsHeaderLeft}>
            <Icon name="message" size={14} color={colors.text_color_secondary} />
            <Text size="s" weight="medium" color="secondary">
              {t('pages.collections.chats')}
            </Text>
            {collectionChats.length > 0 && (
              <Text size="xs" color="tertiary">
                ({collectionChats.length})
              </Text>
            )}
          </View>
        )}

        {collectionChats.length > 0 && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            {isSelectingMultiple ? (
              <>
                <CollectionToolbarButton
                  icon={allSelectedInView ? 'square-check' : 'square'}
                  label={!isXS ? (allSelectedInView ? t('common.clear') : t('common.select')) : undefined}
                  onPress={handleSelectAll}
                  colors={colors}
                  styles={styles}
                />
                <CollectionToolbarButton
                  icon="xmark"
                  label={!isXS ? t('pages.collections.doneSelecting') : undefined}
                  onPress={handleToggleSelectMode}
                  colors={colors}
                  styles={styles}
                />
              </>
            ) : (
              <CollectionToolbarButton
                icon="square-check"
                label={!isXS ? t('pages.collections.selectChats') : undefined}
                onPress={handleToggleSelectMode}
                colors={colors}
                styles={styles}
              />
            )}

            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                {...(Platform.OS === 'web' ? {
                  style: {
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    outline: 'none',
                  },
                } : {})}
              >
                <CollectionToolbarButton
                  icon={currentChatSort.icon}
                  label={!isXS ? currentChatSort.label : undefined}
                  appendIcon="chevron-down"
                  colors={colors}
                  styles={styles}
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
                align="end"
                sideOffset={5}
              >
                {Platform.OS === 'web' ? (
                  CHAT_SORT_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.id}
                      title={option.label}
                      icon={option.icon}
                      isActive={chatSortOrder === option.id}
                      onSelect={() => setChatSortOrder(option.id)}
                    />
                  ))
                ) : (
                  CHAT_SORT_OPTIONS.map((option) => (
                    <DropdownMenu.Item key={option.id} onSelect={() => setChatSortOrder(option.id)}>
                      <DropdownMenu.ItemIcon ios={{
                        name: option.icon,
                        color: chatSortOrder === option.id ? colors.accent_text_color : colors.text_color_primary,
                      }} />
                      <DropdownMenu.ItemTitle>{option.label}</DropdownMenu.ItemTitle>
                    </DropdownMenu.Item>
                  ))
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </View>
        )}
      </View>
      
      {collectionChats.length === 0 ? (
        <View style={styles.emptyChatsContainer}>
          <Text size="m" weight="medium">
            {t('pages.collections.noChatsYet')}
          </Text>
          <Text size="s" color="tertiary">
            {t('pages.collections.noChatsYetDesc').replace('%NAME%', collection?.title || t('collections.defaultName'))}
          </Text>
        </View>
      ) : (
        <View style={styles.chatsList}>
          {collectionChats.map((chat, index) => (
            <CollectionChatItem
              key={chat.id}
              chat={chat}
              isLastItem={index === collectionChats.length - 1}
              isSelectingMultiple={isSelectingMultiple}
              isSelected={selectedChatIds.includes(chat.id)}
              onToggleSelect={toggleSelectChatForMulti}
            />
          ))}
        </View>
      )}
    </View>
  );
});

const CollectionOverflowTrigger = React.memo(({ colors, styles, label }) => (
  <Pressable
    style={({ hovered }) => [
      styles.titleActionButton,
      hovered && styles.titleActionButtonHovered,
    ]}
    accessibilityRole="button"
    accessibilityLabel={label}
  >
    <Icon name="ellipsis" size={16} color={colors.text_color_primary} />
  </Pressable>
));
CollectionOverflowTrigger.displayName = 'CollectionOverflowTrigger';

const CollectionPage = React.memo(({ collection }) => {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams();
  const router = useRouter();
  const { colors } = useTheme();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS, isXL } = useResponsiveness();
  const requireAuth = useRequireAuth();
  const isWorkMode = useCoworkerStore((s) => s.composerMode === 'coworker');
  const setComposerMode = useCoworkerStore((s) => s.setComposerMode);
  const [refreshing, setRefreshing] = useState(false);
  const { addToast } = useToastStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus === true);
  const insets = useSafeAreaInsets();
  const [isNearTop, setIsNearTop] = useState(true);
  const keyboardHeight = useComposerKeyboardHeight();

  const VIEW_MODE_OPTIONS = useMemo(() => [
    { value: VIEW_MODES.GRID, label: t('pages.common.grid'), icon: 'th-large' },
    { value: VIEW_MODES.LIST, label: t('pages.common.list'), icon: 'list' },
  ], [t]);

  const SORT_OPTIONS = useMemo(() => [
    { id: 'newest', label: t('pages.common.newestFirst'), icon: 'arrow-down-wide-short' },
    { id: 'oldest', label: t('pages.common.oldestFirst'), icon: 'arrow-up-wide-short' },
    { id: 'a-z', label: t('pages.common.nameAZ'), icon: 'arrow-down-a-z' },
    { id: 'z-a', label: t('pages.common.nameZA'), icon: 'arrow-up-z-a' },
  ], [t]);

  // File selection state
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);
  const [sortOrder, setSortOrder] = useCollectionContentSort(id, 'files');
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const initializeNewChatWithFiles = useChatInputStore(state => state.initializeNewChatWithFiles);
  const requestInputFocus = useChatInputStore(state => state.requestInputFocus);
  const setInputContext = useChatInputStore(state => state.setContext);
  const setInputSelectedModels = useChatInputStore(state => state.setSelectedModels);
  const setInputParameter = useChatInputStore(state => state.setParameter);

  const { useInputModal, useConfirmationModal } = useModalHelper();
  const showInputModal = useInputModal();
  const showConfirmationModal = useConfirmationModal();

  // Get collection data
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const baseCollection = collectionsMap[id];
  const collectionTitle = baseCollection?.title;
  const collectionCustomInstructions = baseCollection?.customInstructions;
  const collectionIcon = baseCollection?.icon || 'folder';
  const collectionMemoryMode = baseCollection?.memoryMode
    || (baseCollection?.memoryEnabled === false ? 'off' : 'account');
  const collectionMemoryEnabled = collectionMemoryMode !== 'off';
  const collectionDefaults = baseCollection?.defaults;
  useWorkStarterPrompts({ collectionId: id, enabled: collectionMemoryEnabled });

  const allCollections = useMemo(
    () => collectionIds.map((cid) => collectionsMap[cid]).filter(Boolean),
    [collectionIds, collectionsMap],
  );
  const ancestorCollections = useMemo(
    () => getAncestorIds(allCollections, id).map((aid) => collectionsMap[aid]).filter(Boolean),
    [allCollections, id, collectionsMap],
  );
  const childCollections = useMemo(
    () => allCollections.filter((col) => normalizeParentId(col.parentId) === id),
    [allCollections, id],
  );
  
  const createConversation = useChatStore(state => state.createConversation);

  // Chat multi-select state (shared with the library sidebar's bulk actions).
  // Driving the floating action bar from the same store action lets bulk
  // delete / archive / unarchive / add-to-collection reuse the existing
  // optimistic-with-rollback implementations.
  const isSelectingMultipleChats = useChatStore(state => state.isSelectingMultipleChats);
  const selectedChatIds = useChatStore(state => state.selectedChatIds);
  const stopSelectingMultipleChats = useChatStore(state => state.stopSelectingMultipleChats);
  const deleteSelectedChats = useChatStore(state => state.deleteSelectedChats);
  const archiveSelectedChats = useChatStore(state => state.archiveSelectedChats);
  const unarchiveSelectedChats = useChatStore(state => state.unarchiveSelectedChats);
  const removeSelectedChatsFromCollection = useChatStore(state => state.removeSelectedChatsFromCollection);
  const allChatsForSelection = useChatStore(state => state.chats);

  // Only show the chat bulk-action bar for chats that actually live in
  // *this* collection — we don't want a stale cross-context selection (e.g.
  // user selected library chats elsewhere) to take over the collection page.
  const selectedChatsInCollection = useMemo(() => {
    const ids = new Set(baseCollection?.conversationIds || []);
    return selectedChatIds.filter(cid => ids.has(cid));
  }, [selectedChatIds, baseCollection?.conversationIds]);

  const hasArchivedSelected = useMemo(() => {
    if (!selectedChatsInCollection.length) return false;
    return selectedChatsInCollection.some(cid => {
      const chat = allChatsForSelection.find(c => c.id === cid);
      return chat?.isArchived;
    });
  }, [selectedChatsInCollection, allChatsForSelection]);

  // Collection store actions
  const renameCollection = useCollectionStore(state => state.renameCollection);
  const updateCollection = useCollectionStore(state => state.updateCollection);
  const deleteCollection = useCollectionStore(state => state.deleteCollection);
  const addFilesToCollection = useCollectionStore(state => state.addFilesToCollection);
  const removeFileFromCollection = useCollectionStore(state => state.removeFileFromCollection);

  // File store
  const files = useFileStore(state => state.files);
  const deleteFile = useFileStore(state => state.actions.deleteFile);

  const mockConversationId = `new-${baseCollection?.id}`;
  const collectionDraft = useChatInputStore((state) => state.inputStates?.[mockConversationId]?.text || '');
  const hasComposerFiles = useChatInputStore((state) => {
    const files = state.inputStates?.[mockConversationId]?.files;
    return Array.isArray(files) && files.length > 0;
  });
  const setCollectionMessage = useCallback((text) => {
    const composer = useChatInputStore.getState();
    composer.setContext(mockConversationId);
    composer.setText(text);
  }, [mockConversationId]);

  const styles = useMemo(() => createStyles(colors, isXS, isXL, isNearTop, insets.bottom), [colors, isXS, isXL, isNearTop, insets.bottom]);

  // Get collection files with sorting
  const collectionFiles = useMemo(() => {
    if (!baseCollection?.files) return [];
    
    const collectionFileIds = baseCollection.files.map(f => f.id || f);
    let result = collectionFileIds
      .map(fileId => files[fileId] || baseCollection.files.find(f => (f.id || f) === fileId))
      .filter(Boolean);

    // Apply sorting
    result = [...result].sort((a, b) => {
      switch (sortOrder) {
        case 'newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'oldest':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'a-z':
          return (a.name || '').localeCompare(b.name || '');
        case 'z-a':
          return (b.name || '').localeCompare(a.name || '');
        default:
          return 0;
      }
    });

    return result;
  }, [baseCollection?.files, files, sortOrder]);

  useEffect(() => {
    if (activeTab) return;
    setActiveTab('chats');
  }, [activeTab]);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
    } catch (error) {
      console.error('Error refreshing collection:', error);
    } finally {
      setRefreshing(false);
    }
  }, [id]);

  const handleRenameCollection = useCallback(() => {
    showInputModal({ 
      title: t('collections.rename.title'),
      subtitle: t('collections.rename.subtitle'),
      placeholder: t('collections.rename.placeholder'),
      primaryButtonTitle: t('common.save'),
      initialValue: collectionTitle,
      onSave: async (newName) => {
        try {
          await renameCollection(id, newName);
          addToast(t('pages.collections.renamed'), 'success');
        } catch (error) {
          console.error('Failed to rename collection:', error);
          addToast(t('collections.rename.error'), 'error');
        }
      },
    });
  }, [collectionTitle, id, renameCollection, showInputModal, addToast]);

  const handleDeleteCollection = useCallback(() => {
    showConfirmationModal({
      title: t('pages.collections.deleteTitle').replace('%NAME%', collectionTitle || t('collections.defaultName')),
      message: t('pages.collections.deleteMessage'),
      confirmText: t('common.delete'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteCollection(id);
          router.replace('/files');
          addToast(t('pages.collections.deleted'), 'success');
        } catch (error) {
          console.error('Failed to delete collection:', error);
          addToast(t('pages.collections.deleteFailed'), 'error');
        }
      },
    });
  }, [collectionTitle, id, deleteCollection, showConfirmationModal, addToast, router]);

  const handleChangeCollectionIcon = useCallback(() => {
    openModal('CollectionsIconGridModal', {
      selectedIcon: collectionIcon,
      onIconSelect: async (iconKey) => {
        try {
          await updateCollection(id, { icon: iconKey });
          addToast(t('pages.collections.iconUpdated'), 'success');
        } catch (error) {
          console.error('Failed to update collection icon:', error);
          addToast(t('pages.collections.iconUpdateFailed'), 'error');
        }
      },
    });
  }, [openModal, collectionIcon, id, updateCollection, addToast]);

  const handleBackToFiles = useCallback(() => {
    router.push('/files');
  }, [router]);

  const handleNewSubcollection = useCallback(() => {
    const collections = Object.values(collectionsMap || {});
    if (getItemDepth(collections, id) >= MAX_FOLDER_DEPTH - 1) {
      addToast(t('pages.collections.nestedTooDeep'), 'error');
      return;
    }
    openModal('CreateCollectionModal', {
      parentId: id,
      parentTitle: collectionTitle,
    });
  }, [collectionsMap, id, collectionTitle, addToast, t, openModal]);

  const handleMove = useCallback(() => {
    if (!id) return;
    const collections = Object.values(collectionsMap || {});
    const excludeIds = [id, ...getDescendantIds(collections, id)];
    openModal('MoveCollectionModal', {
      collectionId: id,
      excludeIds,
    });
  }, [collectionsMap, id, openModal]);

  const handleOpenInstructions = useCallback(() => {
    openModal('CollectionCustomInstructionsModal', {
      onSave: async (instructions) => {
        try {
          await updateCollection(id, { customInstructions: instructions });
          addToast(t('pages.collections.instructionsUpdated'), 'success');
        } catch (error) {
          console.error('Failed to update collection instructions:', error);
          addToast(t('pages.collections.instructionsUpdateFailed'), 'error');
        }
      },
      collectionTitle: collectionTitle || 'Collection',
      initialInstructions: collectionCustomInstructions || '',
    });
  }, [openModal, id, updateCollection, collectionTitle, collectionCustomInstructions, addToast]);
  const handleOpenSettings = useCallback(() => {
    openModal('CollectionSettingsModal', {
      onSave: async ({ customInstructions, memoryEnabled, defaults }) => {
        try {
          await updateCollection(id, {
            customInstructions,
            memoryEnabled,
            defaults,
          });
          addToast(t('collectionSettings.saved'), 'success');
        } catch (error) {
          console.error('Failed to update collection settings:', error);
          addToast(t('collectionSettings.saveFailed'), 'error');
        }
      },
      collectionTitle: collectionTitle || 'Collection',
      initialDefaults: collectionDefaults || {},
      initialInstructions: collectionCustomInstructions || '',
      initialMemoryEnabled: collectionMemoryEnabled,
    });
  }, [openModal, id, updateCollection, collectionTitle, collectionDefaults, collectionCustomInstructions, collectionMemoryEnabled, addToast, t]);

  const handleAddFiles = useCallback(() => {
    openModal('AddFilesToCollectionModal', {
      collectionId: id,
      collectionTitle: collectionTitle || 'Collection',
    });
  }, [openModal, id, collectionTitle]);

  const handleCreateTask = useCallback(() => {
    openModal('CreateTaskModal', {
      collectionId: id,
      collectionTitle: collectionTitle || 'Collection',
    });
  }, [openModal, id, collectionTitle]);

  const handleCollectionModeChange = useCallback((mode) => {
    if (mode === 'work') {
      if (blockIfWorkModeDisabled()) return;
      const gated = requireAuth(() => true, {
        source: 'collection_composer_coworker_mode',
        title: t('chatInput.coworkerMode.authTitle'),
        subtitle: t('chatInput.coworkerMode.authSubtitle'),
        icon: 'robot',
      });
      if (gated !== true) return;
      if (!isPlus) {
        openModal('PricingModal', {
          title: t('work.plusRequired.title'),
          subtitle: t('work.plusRequired.subtitle'),
        });
        return;
      }
      setComposerMode('coworker');
      return;
    }
    setComposerMode('chat');
  }, [isPlus, openModal, requireAuth, setComposerMode, t]);

  const handleOpenMemorySettings = useCallback(() => {
    openModal('CollectionMemorySettingsModal', {
      collectionId: id,
      collectionTitle: collectionTitle || 'Collection',
      initialMemoryMode: collectionMemoryMode,
      onSaveMode: async (nextMode) => {
        await updateCollection(id, { memoryMode: nextMode });
        addToast(
          t(
            nextMode === 'off'
              ? 'pages.collections.memoryDisabled'
              : nextMode === 'collection'
                ? 'pages.collections.memoryModeCollectionEnabled'
                : 'pages.collections.memoryEnabled'
          ),
          'success',
        );
      },
    });
  }, [openModal, id, collectionTitle, collectionMemoryMode, updateCollection, addToast, t]);

  const handleSend = useCallback(async (formattedMessage) => {
    if (!formattedMessage) return false;

    if (useCoworkerStore.getState().composerMode === 'coworker') {
      const prompt = String(formattedMessage.text || '').trim();
      if (!prompt) return false;
      try {
        const coworkerState = useCoworkerStore.getState();
        const created = await coworkerState.createTask({
          prompt,
          fileIds: Array.isArray(formattedMessage.fileIds) ? formattedMessage.fileIds : [],
          ...coworkerState.getComposerLaunchOptions(),
          includeMemory: collectionMemoryEnabled && coworkerState.composerIncludeMemory,
          collectionId: id,
        });
        navigateToCreatedWork(router, created);
        return true;
      } catch (error) {
        reportWorkLaunchError(error, {
          source: 'collection_composer',
          fallback: t('pages.collections.createFailed'),
        });
        return false;
      }
    }

    // Capture the collection composer state BEFORE the async create call so we
    // can transfer the seeded defaults (model / web search / image params) into
    // the newly created conversation's context. ChatInput.clearInput() runs
    // synchronously right after onSend, which would otherwise wipe these.
    const newContextState = useChatInputStore.getState().inputStates?.[mockConversationId];
    const savedSelectedParameters = newContextState?.selectedParameters || {};
    const savedSelectedButtons = newContextState?.selectedButtons || [];

    try {
      if (!socketManager.isSocketConnected()) {
        addToast(t('pages.common.connectionError'), 'error', [], { category: 'connection' });
        return false;
      }

      const conversation = await createConversation({
        userId: userId,
        formattedMessage: formattedMessage,
        collectionId: id,
      });
      
      if (!conversation) {
        throw new Error('Failed to create conversation');
      }

      // Persist the collection defaults into the new conversation's context so
      // they carry over for the whole chat (not just the first message).
      try {
        const hasModels = formattedMessage.modelIds?.length > 0;
        const hasParameters = Object.keys(savedSelectedParameters).length > 0;
        const hasButtons = savedSelectedButtons.length > 0;
        if (hasModels || hasParameters || hasButtons) {
          const chatInputStore = useChatInputStore.getState();
          chatInputStore.setContext(conversation.id);
          if (hasModels) {
            chatInputStore.setSelectedModels(formattedMessage.modelIds, { clearAgents: false });
          }
          // Must run AFTER setSelectedModels, which prunes parameters to the
          // selected model set.
          if (hasParameters || hasButtons) {
            useChatInputStore.setState(state => ({
              inputStates: {
                ...state.inputStates,
                [conversation.id]: {
                  ...state.inputStates[conversation.id],
                  ...(hasParameters ? { selectedParameters: savedSelectedParameters } : {}),
                  ...(hasButtons ? { selectedButtons: savedSelectedButtons } : {}),
                }
              }
            }));
          }
        }
      } catch (transferError) {
        console.error('Failed to transfer collection defaults to new chat:', transferError);
      }

      setTimeout(() => {
        socketManager.emitKickConversation(userId, conversation.id);
      }, 250);

      router.push(`/c/${conversation.id}`);
      return true;
    } catch (error) {
      console.error('Error creating conversation:', error);
      addToast(error.message || t('pages.collections.createFailed'), 'error');
      // Keep the composer text so the user can retry without retyping.
      return false;
    }
  }, [router, createConversation, addToast, userId, id, mockConversationId, collectionMemoryEnabled, t]);

  // Seed the new-chat composer for this collection from its saved defaults.
  // These are pre-applied but remain user-overridable (the model selector,
  // web-search toggle and parameter controls stay fully usable). We re-seed
  // when the collection's defaults change (e.g. right after saving settings)
  // so the composer reflects the latest configuration. Only the chat model,
  // web search and image parameters are touched — never the user's text.
  const seedSignatureRef = useRef(null);
  useEffect(() => {
    if (!baseCollection?.id) return;

    const signature = JSON.stringify({
      id: baseCollection.id,
      defaults: collectionDefaults || null,
      work: isWorkMode,
    });
    if (seedSignatureRef.current === signature) return;
    seedSignatureRef.current = signature;

    // Chat defaults (model, web search, locked params) only apply to Chat.
    if (isWorkMode) return;

    if (!collectionDefaults) return;

    try {
      setInputContext(mockConversationId);

      const defaultModelId = collectionDefaults.model;
      if (defaultModelId) {
        setInputSelectedModels([defaultModelId], { clearAgents: false });
      }

      // Programmatic seed from collection defaults — don't overwrite the
      // user's persisted global web-search preference.
      setInputParameter('tools', collectionDefaults.webSearchEnabled ? ['search-web'] : null, { syncWebSearchPreference: false });

      const modelParameters = collectionDefaults.modelParameters || {};
      if (defaultModelId && modelParameters && typeof modelParameters === 'object') {
        Object.entries(modelParameters).forEach(([name, value]) => {
          setInputParameter(`${defaultModelId}:${name}`, value);
        });
      }
    } catch (error) {
      console.error('Failed to seed collection chat defaults:', error);
    }
  }, [baseCollection?.id, collectionDefaults, mockConversationId, isWorkMode, setInputContext, setInputSelectedModels, setInputParameter]);

  // File selection handlers
  const handleFileClick = useCallback((file) => {
    openModal('FileEditModal', { fileId: file.id });
  }, [openModal]);

  const handleFileSelect = useCallback((file) => {
    setSelectedFiles((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(file.id)) {
        newSelection.delete(file.id);
      } else if (newSelection.size < 10) {
        newSelection.add(file.id);
      }
      return newSelection;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  const handleStartChat = useCallback(() => {
    const selectedFileIds = Array.from(selectedFiles);
    initializeNewChatWithFiles(selectedFileIds);
    router.push('/app');
  }, [selectedFiles, initializeNewChatWithFiles, router]);

  const handleDeleteSelected = useCallback(() => {
    const count = selectedFiles.size;
    showConfirmationModal({
      title: t(count > 1 ? 'pages.collections.removeFilesTitle' : 'pages.collections.removeFileTitle').replace('%COUNT%', count),
      message: t(count > 1 ? 'pages.collections.removeFilesMessage' : 'pages.collections.removeFileMessage').replace('%COUNT%', count),
      confirmText: t('pages.common.remove'),
      isDangerous: true,
      onConfirm: async () => {
        setIsDeleting(true);
        const selectedFileIds = Array.from(selectedFiles);
        let successCount = 0;
        let failCount = 0;
        
        for (const fileId of selectedFileIds) {
          try {
            await removeFileFromCollection(id, fileId);
            successCount++;
          } catch (error) {
            console.error('Error removing file:', error);
            failCount++;
          }
        }
        
        setIsDeleting(false);
        setSelectedFiles(new Set());
        
        if (failCount === 0) {
          addToast(t(successCount > 1 ? 'pages.collections.filesRemoved' : 'pages.collections.fileRemoved').replace('%COUNT%', successCount), 'success');
        } else if (successCount === 0) {
          addToast(t(failCount > 1 ? 'pages.collections.removeFilesFailed' : 'pages.collections.removeFileFailed').replace('%COUNT%', failCount), 'error');
        } else {
          addToast(t(successCount > 1 ? 'pages.collections.removePartial' : 'pages.collections.removePartialSingle').replace('%SUCCESS%', successCount).replace('%FAIL%', failCount), 'warning');
        }
      }
    });
  }, [selectedFiles, removeFileFromCollection, id, addToast, showConfirmationModal]);

  const handleViewModeToggle = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  // The global selectedChatIds could in theory include chats picked from the
  // library sidebar (if multi-select was started there). Before any bulk
  // action we re-trim the global selection to only the chats actually in
  // this collection so the existing bulk store actions (which read from
  // selectedChatIds) can't accidentally affect unrelated chats.
  const scopeSelectionToCollection = useCallback(() => {
    const allowed = new Set(baseCollection?.conversationIds || []);
    const trimmed = useChatStore
      .getState()
      .selectedChatIds.filter(cid => allowed.has(cid));
    useChatStore.setState({ selectedChatIds: trimmed });
    return trimmed;
  }, [baseCollection?.conversationIds]);

  // Chat bulk action handlers — keyed off the global multi-select state.
  // Each runs through the existing confirmation modal pattern so destructive
  // actions match the rest of the app.
  const handleBulkDeleteChats = useCallback(() => {
    const count = selectedChatsInCollection.length;
    if (count === 0) return;
    showConfirmationModal({
      title: t(count === 1 ? 'pages.collections.deleteChatTitle' : 'pages.collections.deleteChatsTitle').replace('%COUNT%', count),
      message: t('pages.collections.deleteChatsMessage'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          const ids = scopeSelectionToCollection();
          if (ids.length === 0) return;
          await deleteSelectedChats();
          addToast(
            t(ids.length === 1 ? 'pages.collections.chatDeleted' : 'pages.collections.chatsDeleted').replace('%COUNT%', ids.length),
            'success',
          );
        } catch (error) {
          console.error('Error bulk deleting chats:', error);
          addToast(t('chat.delete.error'), 'error');
        }
      },
    });
  }, [selectedChatsInCollection.length, deleteSelectedChats, scopeSelectionToCollection, showConfirmationModal, addToast, t]);

  const handleBulkArchiveChats = useCallback(() => {
    const count = selectedChatsInCollection.length;
    if (count === 0) return;
    const isUnarchive = hasArchivedSelected;
    showConfirmationModal({
      title: t(
        isUnarchive
          ? (count === 1 ? 'pages.collections.unarchiveChatTitle' : 'pages.collections.unarchiveChatsTitle')
          : (count === 1 ? 'pages.collections.archiveChatTitle' : 'pages.collections.archiveChatsTitle'),
      ).replace('%COUNT%', count),
      message: t(isUnarchive ? 'chat.archive.unarchiveMessage' : 'chat.archive.message'),
      confirmText: isUnarchive ? t('chat.archive.unarchiveButton') : t('common.archive'),
      cancelText: t('common.cancel'),
      isDangerous: !isUnarchive,
      onConfirm: async () => {
        try {
          const ids = scopeSelectionToCollection();
          if (ids.length === 0) return;
          if (isUnarchive) {
            await unarchiveSelectedChats();
            addToast(
              t(ids.length === 1 ? 'pages.collections.chatUnarchived' : 'pages.collections.chatsUnarchived').replace('%COUNT%', ids.length),
              'success',
            );
          } else {
            await archiveSelectedChats();
            addToast(
              t(ids.length === 1 ? 'pages.collections.chatArchived' : 'pages.collections.chatsArchived').replace('%COUNT%', ids.length),
              'success',
            );
          }
        } catch (error) {
          console.error('Error bulk archiving chats:', error);
          addToast(t('chat.archive.error'), 'error');
        }
      },
    });
  }, [
    selectedChatsInCollection.length,
    hasArchivedSelected,
    archiveSelectedChats,
    unarchiveSelectedChats,
    scopeSelectionToCollection,
    showConfirmationModal,
    addToast,
    t,
  ]);

  const handleBulkRemoveChatsFromCollection = useCallback(() => {
    const count = selectedChatsInCollection.length;
    if (count === 0 || !id) return;
    showConfirmationModal({
      title: t(count === 1 ? 'pages.collections.removeChatTitle' : 'pages.collections.removeChatsTitle').replace('%COUNT%', count),
      message: t('pages.collections.removeChatsMessage'),
      confirmText: t('pages.common.remove'),
      cancelText: t('common.cancel'),
      onConfirm: async () => {
        try {
          const ids = scopeSelectionToCollection();
          if (ids.length === 0) return;
          const result = await removeSelectedChatsFromCollection(id);
          const failedCount = result?.failedIds?.length || 0;
          const successCount = result?.successCount ?? ids.length;
          if (failedCount === 0) {
            addToast(
              t(successCount === 1 ? 'pages.collections.chatRemovedFromCollection' : 'pages.collections.chatsRemovedFromCollection').replace('%COUNT%', successCount),
              'success',
            );
          } else if (successCount === 0) {
            addToast(
              t(failedCount === 1 ? 'pages.collections.removeChatFailed' : 'pages.collections.removeChatsFailed').replace('%COUNT%', failedCount),
              'error',
            );
          } else {
            addToast(
              t('pages.collections.removeChatsPartial')
                .replace('%SUCCESS%', successCount)
                .replace('%FAIL%', failedCount),
              'warning',
            );
          }
        } catch (error) {
          console.error('Error bulk removing chats from collection:', error);
          addToast(
            t(count === 1 ? 'pages.collections.removeChatFailed' : 'pages.collections.removeChatsFailed').replace('%COUNT%', count),
            'error',
          );
        }
      },
    });
  }, [selectedChatsInCollection.length, id, removeSelectedChatsFromCollection, scopeSelectionToCollection, showConfirmationModal, addToast, t]);

  const handleBulkMoveChatsToCollection = useCallback(() => {
    const ids = scopeSelectionToCollection();
    if (ids.length === 0 || !id) return;
    openModal('MoveChatsToCollectionModal', {
      chatIds: ids,
      excludeCollectionId: id,
    });
  }, [id, openModal, scopeSelectionToCollection]);

  const handleClearChatSelection = useCallback(() => {
    stopSelectingMultipleChats();
  }, [stopSelectingMultipleChats]);

  // Always exit chat select mode when this collection page unmounts so the
  // selection state doesn't leak into the rest of the app.
  useEffect(() => {
    return () => {
      try {
        useChatStore.getState().stopSelectingMultipleChats?.();
      } catch (_) {}
    };
  }, []);

  // Switching tabs away from chats also exits select mode for clarity.
  useEffect(() => {
    if (activeTab && activeTab !== 'chats' && isSelectingMultipleChats) {
      stopSelectingMultipleChats();
    }
  }, [activeTab, isSelectingMultipleChats, stopSelectingMultipleChats]);

  if (!baseCollection) {
    return (
      <View style={styles.container}>
        <StatusContainer
          type='error'
          title={t('pages.collections.notFound')}
          actionText={t('pages.collections.goToFiles')}
          onAction={() => router.push('/files')}
        />
      </View>
    );
  }

  const iconData = collectionIcons[collectionIcon] || collectionIcons.folder;
  const currentSort = SORT_OPTIONS.find(o => o.id === sortOrder) || SORT_OPTIONS[0];

  return (
    <View style={styles.container}>
      <View style={styles.contentWrapper}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.modeSwitch}>
              <ComposerModeSwitch
                value={isWorkMode ? 'work' : 'chat'}
                onChange={handleCollectionModeChange}
                compact
                pillsOnly
              />
            </View>
          </View>
        </View>

        <ScrollView 
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContainerContent}
          showsVerticalScrollIndicator={false}
          onScroll={(e) => setIsNearTop(e.nativeEvent.contentOffset.y < 10)}
          scrollEventThrottle={16}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.text_color_secondary}
              colors={[colors.text_color_secondary]}
            />
          }
        >
          <View style={styles.content}>
            <View style={styles.collectionSection}>
              {ancestorCollections.length > 0 && (
                <View style={styles.breadcrumbContainer}>
                  <Pressable
                    style={({ hovered }) => [
                      styles.breadcrumbButton,
                      hovered && styles.breadcrumbButtonHovered
                    ]}
                    onPress={handleBackToFiles}
                  >
                    <Icon name="arrow-left" size={14} color={colors.text_color_secondary} />
                    <Text size="s" color="secondary">{t('pages.common.allFiles')}</Text>
                  </Pressable>
                  {ancestorCollections.map((ancestor) => (
                    <React.Fragment key={ancestor.id}>
                      <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} style={styles.breadcrumbSeparator} />
                      <Pressable
                        style={({ hovered }) => [
                          styles.breadcrumbButton,
                          hovered && styles.breadcrumbButtonHovered
                        ]}
                        onPress={() => router.push(`/collections/${ancestor.id}`)}
                      >
                        <Text size="s" color="secondary" numberOfLines={1} style={styles.breadcrumbLabel}>{ancestor.title}</Text>
                      </Pressable>
                    </React.Fragment>
                  ))}
                </View>
              )}

              <View style={styles.titleRow}>
                <View style={styles.titleIdentity}>
                  <Icon name={iconData.icon} size={22} color={colors.text_color_primary} solid />
                  <Text size="xxl" weight="semibold" numberOfLines={1} style={styles.breadcrumbLabel}>
                    {collectionTitle}
                  </Text>
                </View>
                {!isXS || Platform.OS === 'web' ? (
                  <View style={styles.titleActions}>
                    <CollectionDropdown
                      TriggerComponent={() => (
                        <CollectionOverflowTrigger
                          colors={colors}
                          styles={styles}
                          label={t('nav.routes.collection.moreOptions')}
                        />
                      )}
                      handleSettings={handleOpenSettings}
                      handleCustomInstructions={handleOpenInstructions}
                      handleMemorySettings={handleOpenMemorySettings}
                      handleAddFiles={handleAddFiles}
                      handleCreateTask={handleCreateTask}
                      handleChangeIcon={handleChangeCollectionIcon}
                      handleRename={handleRenameCollection}
                      handleDelete={handleDeleteCollection}
                      handleNewSubcollection={handleNewSubcollection}
                      handleMove={handleMove}
                      title={collectionTitle}
                    />
                  </View>
                ) : null}
              </View>

              {!isXS && (
                <>
                  {!isWorkMode && (
                    <WorkModeIntentSuggestions
                      contextId={mockConversationId}
                      onActivate={() => handleCollectionModeChange('work')}
                    />
                  )}
                  {isWorkMode && !String(collectionDraft || '').trim() && (
                    <View style={styles.workSuggestionChips}>
                      <WorkSuggestionChips
                        compact
                        collectionId={id}
                        collectionTitle={collectionTitle}
                        enabled={collectionMemoryEnabled}
                        hasFiles={hasComposerFiles}
                        onSelect={setCollectionMessage}
                      />
                    </View>
                  )}
                  <ChatInput
                    key={`collection-${id}`}
                    conversationId={mockConversationId}
                    collectionId={id}
                    forceInputStyle={CHAT_INPUT_STYLE.FLOATING}
                    placeholder={isWorkMode
                      ? WORK_COMPOSER_PLACEHOLDER
                      : t('pages.collections.newChatPlaceholderNamed').replace('%NAME%', collectionTitle || t('collections.defaultName'))}
                    onSend={handleSend}
                    isWorkMode={isWorkMode}
                    hideVoiceMode={isWorkMode}
                  />
                </>
              )}

              {/* Start Chat Button - Mobile only: focuses the collection-scoped composer mounted at the bottom of the page */}
              {isXS && (
                <Button
                  text={t('pages.collections.startChat')}
                  variant="primary"
                  size="large"
                  prependIcon="message"
                  onPress={() => {
                    // Synchronous focus call keeps us inside the user-gesture tick
                    // so iOS mobile Safari actually opens the soft keyboard.
                    const focused = focusChatInput(mockConversationId);
                    // Also bump the store signal as an async fallback (e.g. if the
                    // input hasn't mounted yet).
                    if (!focused) requestInputFocus?.(mockConversationId);
                  }}
                  style={{ marginBottom: 16, width: '100%' }}
                />
              )}

              {childCollections.length > 0 && (
                <View style={styles.nestedSection}>
                  <View style={styles.nestedHeader}>
                    <Text size="s" weight="medium" color="secondary">
                      {t('pages.collections.subcollections')}
                    </Text>
                    <Pressable
                      onPress={handleNewSubcollection}
                      hitSlop={8}
                      style={({ hovered }) => [
                        styles.nestedAddButton,
                        hovered && styles.titleActionButtonHovered,
                        Platform.OS === 'web' ? { cursor: 'pointer' } : null,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={t('pages.collections.newSubcollection')}
                    >
                      <Icon name="folder-plus" size={14} color={colors.text_color_secondary} />
                    </Pressable>
                  </View>
                  {childCollections.map((child) => (
                    <Pressable
                      key={child.id}
                      onPress={() => router.push(`/collections/${child.id}`)}
                      style={({ hovered }) => [styles.nestedRow, hovered && styles.nestedRowHovered]}
                    >
                      <Icon name={collectionIcons[child.icon]?.icon || 'folder'} size={16} color={colors.text_color_secondary} />
                      <Text size="s" weight="medium" numberOfLines={1} style={{ flex: 1 }}>{child.title}</Text>
                      <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />
                    </Pressable>
                  ))}
                </View>
              )}

              {/* Content Tabs */}
              <View style={styles.tabsContainer}>
                <View style={styles.tabsRow}>
                  {[
                    { id: 'chats', label: t('pages.collections.chats') },
                    { id: 'files', label: t('pages.collections.sources') },
                    { id: 'tasks', label: t('pages.collections.tasks') },
                  ].map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <Pressable
                        key={tab.id}
                        onPress={() => setActiveTab(tab.id)}
                        style={({ hovered }) => [
                          styles.tabButton,
                          hovered && !isActive && styles.tabButtonHover,
                          isActive && styles.tabButtonActive,
                        ]}
                        accessibilityRole="tab"
                        accessibilityState={{ selected: isActive }}
                        accessibilityLabel={tab.label}
                      >
                        <Text
                          size="s"
                          weight={isActive ? 'semibold' : 'medium'}
                          color={isActive ? 'primary' : 'secondary'}
                        >
                          {tab.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              </View>

              {/* Files tab content */}
              {activeTab === 'files' && collectionFiles.length > 0 && (
                <>
                  <View style={[
                    styles.allFilesHeader,
                    isXS && { flexDirection: 'column', alignItems: 'stretch', width: '100%', gap: 12 },
                  ]}>
                    <View style={{ flex: 1 }} />

                    <View style={{
                      flexDirection: 'row',
                      gap: 8,
                      alignItems: 'center',
                      ...(isXS && { justifyContent: 'space-between', width: '100%' }),
                    }}>
                      {/* View Toggle - icons only on mobile (left), full labels on desktop */}
                      {isXS ? (
                        <View style={{ flexDirection: 'row', backgroundColor: colors.solid_color_tertiary, padding: 4, borderRadius: 8, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.border_color_secondary, gap: 0 }}>
                          {VIEW_MODE_OPTIONS.map((option) => (
                            <Pressable
                              key={option.value}
                              onPress={() => handleViewModeToggle(option.value)}
                              style={{
                                paddingHorizontal: 12,
                                paddingVertical: 8,
                                borderRadius: 6,
                                backgroundColor: viewMode === option.value ? colors.solid_color_secondary : 'transparent',
                              }}
                              accessibilityRole="button"
                              accessibilityState={{ selected: viewMode === option.value }}
                              accessibilityLabel={option.label}
                            >
                              <Icon
                                name={option.icon}
                                size={16}
                                color={viewMode === option.value ? colors.text_color_primary : colors.text_color_secondary}
                              />
                            </Pressable>
                          ))}
                        </View>
                      ) : (
                        <Toggle
                          options={VIEW_MODE_OPTIONS}
                          value={viewMode}
                          onChange={handleViewModeToggle}
                          colors={colors}
                          rounded={false}
                        />
                      )}
                      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                        {/* Sort Dropdown */}
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger
                            {...(Platform.OS === 'web' ? {
                              style: {
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                color: 'inherit',
                                font: 'inherit',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                WebkitAppearance: 'none',
                                appearance: 'none',
                                outline: 'none',
                              },
                            } : {})}
                          >
                            <CollectionToolbarButton
                              icon={currentSort.icon}
                              label={!isXS ? currentSort.label : undefined}
                              appendIcon="chevron-down"
                              colors={colors}
                              styles={styles}
                            />
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content
                            className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
                            align="end"
                            sideOffset={5}
                          >
                            {Platform.OS === 'web' ? (
                              SORT_OPTIONS.map((option) => (
                                <DropdownMenuItem
                                  key={option.id}
                                  title={option.label}
                                  icon={option.icon}
                                  isActive={sortOrder === option.id}
                                  onSelect={() => setSortOrder(option.id)}
                                />
                              ))
                            ) : (
                              SORT_OPTIONS.map((option) => (
                                <DropdownMenu.Item key={option.id} onSelect={() => setSortOrder(option.id)}>
                                  <DropdownMenu.ItemIcon ios={{
                                    name: option.icon,
                                    color: sortOrder === option.id ? colors.accent_text_color : colors.text_color_primary,
                                  }} />
                                  <DropdownMenu.ItemTitle>{option.label}</DropdownMenu.ItemTitle>
                                </DropdownMenu.Item>
                              ))
                            )}
                          </DropdownMenu.Content>
                        </DropdownMenu.Root>
                      </View>
                    </View>
                  </View>

                  {viewMode === VIEW_MODES.GRID ? (
                    <FilesGrid
                      files={collectionFiles}
                      selectedFiles={selectedFiles}
                      onFileClick={handleFileClick}
                      onFileSelect={handleFileSelect}
                    />
                  ) : (
                    <FilesTable
                      files={collectionFiles}
                      selectedFiles={selectedFiles}
                      onFileClick={handleFileClick}
                      onFileSelect={handleFileSelect}
                    />
                  )}
                </>
              )}

              {activeTab === 'files' && collectionFiles.length === 0 && (
                <View style={styles.emptyFilesContainer}>
                  <Icon name="files" size={24} color={colors.text_color_tertiary} />
                  <Text size="s" color="secondary">{t('pages.collections.noFilesYet')}</Text>
                  <NavigationBarButton
                    icon="file-circle-plus"
                    label={t('pages.collections.addFiles')}
                    onPress={handleAddFiles}
                    isActive={true}
                  />
                </View>
              )}

              {/* Tasks tab content */}
              {activeTab === 'tasks' && (
                <CollectionTasksSection
                  collectionId={id}
                  colors={colors}
                  isXS={isXS}
                  onCreateTask={handleCreateTask}
                  embedded
                />
              )}

              {/* Chats tab content */}
              {activeTab === 'chats' && (
                <CollectionChatsSection
                  collection={baseCollection}
                  colors={colors}
                  isXS={isXS}
                  embedded
                />
              )}
            </View>
          </View>
        </ScrollView>
      </View>

      {/* File selection actions */}
      {selectedFiles.size > 0 && (
        <FloatingSelectButton
          title={t(selectedFiles.size > 1 ? 'pages.common.filesSelected' : 'pages.common.fileSelected').replace('%COUNT%', selectedFiles.size)}
          onClear={handleClearSelection}
          actions={[
            {
              text: t('pages.common.chat'),
              icon: 'message-dots',
              onPress: handleStartChat,
              variant: 'primary',
            },
            {
              text: t('pages.common.remove'),
              icon: 'folder-minus',
              onPress: handleDeleteSelected,
              variant: 'danger',
            },
          ]}
        />
      )}

      {/* Chat bulk-select actions */}
      {isSelectingMultipleChats && selectedChatsInCollection.length > 0 && selectedFiles.size === 0 && (
        <FloatingSelectButton
          title={t(selectedChatsInCollection.length > 1 ? 'pages.common.chatsSelected' : 'pages.common.chatSelected').replace('%COUNT%', selectedChatsInCollection.length)}
          onClear={handleClearChatSelection}
          actions={[
            {
              text: hasArchivedSelected ? t('dropdowns.unarchive') : t('common.archive'),
              icon: hasArchivedSelected ? 'box-open' : 'box-archive',
              onPress: handleBulkArchiveChats,
              variant: 'secondary',
            },
            {
              text: isXS ? t('pages.common.move') : t('dropdowns.moveToCollection'),
              icon: 'folder-arrow-down',
              onPress: handleBulkMoveChatsToCollection,
              variant: 'secondary',
              accessibilityLabel: t('dropdowns.moveToCollection'),
            },
            {
              text: t('pages.common.remove'),
              icon: 'folder-minus',
              onPress: handleBulkRemoveChatsFromCollection,
              variant: 'secondary',
            },
            {
              text: t('common.delete'),
              icon: 'trash',
              onPress: handleBulkDeleteChats,
              variant: 'danger',
            },
          ]}
        />
      )}

      {/* Mobile Chat Input — hidden while either selection bar is active so
          it doesn't visually compete with the floating actions. */}
      {isXS && selectedFiles.size === 0 && !(isSelectingMultipleChats && selectedChatsInCollection.length > 0) && (
        <View style={{ backgroundColor: colors.solid_color_primary, paddingTop: 8, paddingBottom: Math.max(keyboardHeight, insets.bottom) }}>
          <ChatInputContainer>
            {!isWorkMode && (
              <WorkModeIntentSuggestions
                contextId={mockConversationId}
                onActivate={() => handleCollectionModeChange('work')}
              />
            )}
            {isWorkMode && !String(collectionDraft || '').trim() && (
              <View style={styles.workSuggestionChips}>
                <WorkSuggestionChips
                  compact
                  collectionId={id}
                  collectionTitle={collectionTitle}
                  enabled={collectionMemoryEnabled}
                  hasFiles={hasComposerFiles}
                  onSelect={setCollectionMessage}
                />
              </View>
            )}
            <ChatInput
              key={`collection-mobile-${id}`}
              conversationId={mockConversationId}
              collectionId={id}
              forceInputStyle={CHAT_INPUT_STYLE.FLOATING}
              placeholder={isWorkMode
                ? WORK_COMPOSER_PLACEHOLDER
                : t('pages.collections.newChatPlaceholderNamed').replace('%NAME%', collectionTitle || t('collections.defaultName'))}
              onSend={handleSend}
              isWorkMode={isWorkMode}
              hideVoiceMode={isWorkMode}
            />
          </ChatInputContainer>
        </View>
      )}
    </View>
  );
});

function CollectionRoute() {
  const { t } = useTranslation();
  const { id: collectionId } = useLocalSearchParams();
  const { colors } = useTheme();
  
  const getDeepCollectionById = useCollectionStore(state => state.getDeepCollectionById);
  const fetchCollectionById = useCollectionStore(state => state.fetchCollectionById);
  const isLoadingCollection = useCollectionStore(state => state.isLoading.fetch);

  const isAuthHydrated = useAuthHydrated();
  const isAuthenticated = useAuthPresentation();

  const collection = getDeepCollectionById(collectionId);
  const [isWaitingForCollection, setIsWaitingForCollection] = useState(!collection);
  const hasFetchedRef = useRef({});

  useEffect(() => {
    let mounted = true;

    const loadCollection = async () => {
      if (!isAuthHydrated) return;

      if (!isAuthenticated) {
        if (mounted && !collection) {
          setIsWaitingForCollection(false);
        }
        return;
      }

      if (hasFetchedRef.current[collectionId]) {
        if (mounted && !collection) {
          setIsWaitingForCollection(false);
        }
        return;
      }

      hasFetchedRef.current[collectionId] = true;

      try {
        await fetchCollectionById(collectionId, true);
      } catch (error) {
        console.error('Error loading collection:', error);
      } finally {
        if (mounted && !collection) {
          setIsWaitingForCollection(false);
        }
      }
    };

    if (mounted) {
      loadCollection();
    }

    return () => {
      mounted = false;
    };
  }, [
    collection,
    collectionId,
    fetchCollectionById,
    isAuthHydrated,
    isAuthenticated,
  ]);

  if (!isAuthHydrated || isWaitingForCollection) {
    return (
      <View style={loadingStyles.container}>
        <ActivityIndicator size="large" color={colors.text_color_secondary} />
      </View>
    );
  }

  // Anonymous visitors: collections are private resources. Replace the
  // whole detail view with a sign-in CTA so we don't surface a "no
  // collection found" error or render a half-broken page.
  if (!isAuthenticated) {
    return (
      <>
        <MetaHead title={t('pages.collections.title')} description={t('pages.collections.viewDescription')} noindex />
        <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
          <SignInCTAPanel
            icon="folder-tree"
            source="collections_detail"
            title="Sign in to open this collection"
            subtitle="Collections are private. Create an account or log in to see what's inside."
          />
        </View>
      </>
    );
  }

  return (
    <>
      <MetaHead title={collection?.name ? `${collection.name} - ${t('pages.collections.title')}` : t('pages.collections.title')} description={t('pages.collections.viewDescription')} noindex />
      <CollectionPage collection={collection} />
    </>
  );
}

export default React.memo(CollectionRoute);
